<script setup>
import { onMounted, ref } from 'vue'
import { formatDateLocale } from '@/libs/utils/date.js'
import { useRoute, useRouter } from 'vue-router'
import { getWorkflows } from '@/services/workflowService.js'
import { getCampaigns, getColumns } from '@/services/campaignService.js'

import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'

import { client } from '@/services/fetch.js'
import { usePageNumberStore } from '@/services/store.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faKey, faPencil, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import { toast } from '@/services/toast.js'

library.add(faPencil, faKey, faTrash, faPlus)

const router = useRouter()
const route = useRoute()
const pageNumberStore = usePageNumberStore()
const campaigns = ref({
  data: [],
  pagination: {},
  columns: []
})
const isLoading = ref(true)
const currentPage = ref(1)
const networkError = ref({
  enabled: false,
  message: 'Errore nel caricamento dei dati, riprova più tardi'

})
const workflowLabels = ref({})

const showModal = ref(false)
const campaignToDelete = ref(null)

onMounted(async () => {
  campaigns.value.columns = await getColumns()
  await loadCampaigns(pageNumberStore.getPage(route.name))

  workflowLabels.value = await getWorkflows('campaign').then((result) => {
    return result.reduce((acc, item) => {
      acc[item.key] = item.label
      return acc
    }, {})
  })
})

const loadCampaigns = async (page) => {
  try {
    const campagne = await getCampaigns(page, campaignType.value)

    campaigns.value.data = campagne.data
    campaigns.value.pagination = campagne.pagination
    isLoading.value = false
  } catch (error) {
    networkError.value.enabled = true
    isLoading.value = false
  }
}

const changePage = async (page) => {
  isLoading.value = true
  currentPage.value = page
  await loadCampaigns(page)
  isLoading.value = false
}

const confirmDelete = (campaignId) => {
  campaignToDelete.value = campaignId
  showModal.value = true
}

const deleteCampaign = async () => {
  try {
    networkError.value.enabled = false
    let url = `/campaign/api/campaign/${campaignToDelete.value}`
    await client.delete(url)
    showModal.value = false
    campaignToDelete.value = null
    await loadCampaigns(currentPage.value)
    if (campaigns.value.data.length === 0 && currentPage.value > 1) {
      currentPage.value--
      isLoading.value = true
      await loadCampaigns(currentPage.value)
    }

    toast.create('La campagna è stata eliminata con successo')
  } catch (error) {
    showModal.value = false
    networkError.value.enabled = true
    campaignToDelete.value = null
    toast.create('Qualcosa è andato storto, riprova più tardi', 'error')
  }
}

const addPermission = (campaignId) => {
  router.push({ name: 'admin/Permissions', params: { id: campaignId, type: 'campaign' } })
}

const editCampaign = (campaignId) => {
  router.push({ name: 'admin/Campaign--edit', params: { id: campaignId } })
}
</script>

<template>
  <div class="container-fluid px-0">
    <div class="card">
      <div class="card-body px-0">

        <loading v-if="isLoading"></loading>

        <div v-else class="table-responsive">
          <div v-if="campaigns.data.length === 0" class="alert alert-warning mt-2 mx-2" role="alert">
            Nessuna campagna disponibile.
          </div>
          <div class="d-flex justify-content-end pe-1">
            <!-- TODO - CONTROLLO PERMESSI AZIONI -->
            <!--            <router-link :to="{name: 'Campaign&#45;&#45;new'}" class="btn btn-primary me-2">-->
            <!--              <font-awesome-icon icon="plus"></font-awesome-icon>-->
            <!--            </router-link>-->
          </div>
          <table class="table table-striped">
            <thead>
            <tr>
              <th class=" border-top-0" v-for="item in campaigns.columns" :key="item.field">
                {{ item.label }}
              </th>
              <!-- TODO - CONTROLLO PERMESSI AZIONI -->
              <th class=" border-top-0" v-if="true">
                Azioni
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="campaign in campaigns.data" :key="campaign.id">
              <td v-for="item in campaigns.columns" :key="item.id">
                <span v-if="item.field === 'poll.title'">{{ campaign.poll.title }}</span>

                <span v-else-if="item.field === 'createdAt' || item.field === 'updatedAt'">
                  {{ formatDateLocale(campaign[item.field]) }}
                </span>

                <span v-else-if="item.field === 'pollTemplateRevisionNumber'">
                   {{
                    item.fixed
                      ? campaign['pollTemplateRevisionNumber']
                      : ('Corrente (' + campaign.poll['revisionNumber'] + ')')
                  }}
                </span>

                <span v-else-if="item.field === 'workflow'">
                  {{ workflowLabels[campaign.workflow] || campaign.workflow }}
                </span>

                <span v-else>{{ campaign[item.field] }}</span>
              </td>
              <!-- TODO - CONTROLLO PERMESSI AZIONI -->
              <td v-if="true">
                <div class="d-flex">
                  <button v-if="true" @click="editCampaign(campaign.id)" class="btn btn-primary btn-sm"
                          title="Modifica campagna">
                    <font-awesome-icon icon=" fa-pencil" />
                  </button>

                  <button v-if="true" @click="addPermission(campaign.id)" class="btn btn-primary btn-sm ms-1"
                          title="Assegna permessi">
                    <font-awesome-icon icon=" fa-key" />
                  </button>

                  <button v-if="true" @click="confirmDelete(campaign.id)" class="btn btn-danger btn-sm ms-1"
                          title="Elimina Campagna">
                    <font-awesome-icon icon=" fa-trash" />
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="d-flex justify-content-flex-start m-2">
            <Paginazione v-if="!networkError.enabled"
                         :total-pages="
                Math.ceil(campaigns.pagination.total_items / campaigns.pagination.items_per_page)
              "
                         @page-changed="changePage"
            />
          </div>
        </div>

        <transition name="modal">
          <div v-if="showModal" class="modal fade show d-block mt-5" tabindex="-1" @click.self="showModal = false">
            <div class="modal-dialog">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Conferma Eliminazione</h5>
                  <button type="button" class="btn-close" @click="showModal = false" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                  <p>Sei sicuro di voler eliminare questa campagna?</p>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="showModal = false">Annulla</button>
                  <button type="button" class="btn btn-danger" @click="deleteCampaign">Elimina</button>
                </div>
              </div>
            </div>
          </div>
        </transition>
        <transition name="modal-backdrop">
          <div v-if="showModal" class="modal-backdrop fade show"></div>
        </transition>

      </div>
    </div>
  </div>
</template>

<style lang="scss">
.fa-pencil, .fa-key, .fa-trash {
  width: 1rem;
}

.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-backdrop-enter-active, .modal-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.modal-backdrop-enter-from, .modal-backdrop-leave-to {
  opacity: 0;
}

</style>
